import React, { useEffect, useState, useCallback } from "react";
import {
  Breadcrumb,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Collapse,
  Nav,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faSave } from "@fortawesome/free-solid-svg-icons";
import { useCookies } from "react-cookie";

import {
  getLotoRegionSetting,
  updateLotoRegionSetting,
  getLotoCutoffSetting,
  updateLotoCutoffSetting,
  getLotoChannelSetting,
  updateLotoChannelSetting,
} from "../../../controller/api/__loto__/setting";
import { ChannelTable } from "../channel/partials/ChannelTable";

const CHANNEL_REGIONS = [
  { key: "MN", label: "Miền Nam" },
  { key: "MT", label: "Miền Trung" },
  { key: "MB", label: "Miền Bắc" },
];

const DEFAULT_CUTOFF_TIMES = {
  MB: "16:15",
  MT: "17:10",
  MN: "16:10",
};

const LotoSetting = () => {
  const [cookies] = useCookies(["lottmekong"]);
  const [activeTab, setActiveTab] = useState("setting");
  const [channelRegion, setChannelRegion] = useState("MN");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingChannels, setSavingChannels] = useState({});
  const [message, setMessage] = useState(null);
  const [config, setConfig] = useState({
    MB: false,
    MT: false,
    MN: false,
  });
  const [cutoffTimes, setCutoffTimes] = useState(DEFAULT_CUTOFF_TIMES);
  const [channelsByRegion, setChannelsByRegion] = useState({
    MB: [],
    MT: [],
    MN: [],
  });

  const fetchChannels = useCallback(
    async (region) => {
      try {
        const result = await getLotoChannelSetting(
          cookies.lottmekong,
          region,
        );
        if (result && result.status === 1 && result.channels) {
          setChannelsByRegion((prev) => ({
            ...prev,
            [region]: result.channels,
          }));
        }
      } catch (err) {
        console.log(`fetchChannels ${region} error`, err);
      }
    },
    [cookies.lottmekong],
  );

  useEffect(() => {
    document.title = "Cài đặt Lô tô";
  }, []);

  useEffect(() => {
    const fetchSetting = async () => {
      try {
        const result = await getLotoRegionSetting(cookies.lottmekong);
        if (result && result.config) {
          setConfig(result.config);
          setCutoffTimes(result.cutoffTimes || DEFAULT_CUTOFF_TIMES);
          const enabledRegions = Object.keys(result.config).filter(
            (key) => result.config[key],
          );
          await Promise.all(enabledRegions.map(fetchChannels));
        } else {
          const cutoffResult = await getLotoCutoffSetting(cookies.lottmekong);
          if (cutoffResult && cutoffResult.cutoffTimes) {
            setCutoffTimes(cutoffResult.cutoffTimes);
          }
        }
      } catch (err) {
        console.log("fetchLotoSetting error", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSetting();
  }, [cookies.lottmekong, fetchChannels]);

  const handleToggle = async (region) => {
    const newEnabled = !config[region];
    setConfig((prev) => ({
      ...prev,
      [region]: newEnabled,
    }));
    setMessage(null);

    if (newEnabled && channelsByRegion[region].length === 0) {
      await fetchChannels(region);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const result = await updateLotoRegionSetting(cookies.lottmekong, {
        ...config,
        cutoffTimes,
      });
      const cutoffResult = await updateLotoCutoffSetting(
        cookies.lottmekong,
        cutoffTimes,
      );
      if (
        result &&
        result.status === 1 &&
        cutoffResult &&
        cutoffResult.status === 1
      ) {
        setCutoffTimes(result.cutoffTimes || cutoffResult.cutoffTimes || cutoffTimes);
        setMessage({ type: "success", text: "Cập nhật thành công!" });
      } else {
        setMessage({ type: "danger", text: "Cập nhật thất bại!" });
      }
    } catch (err) {
      console.log("updateLotoSetting error", err);
      setMessage({ type: "danger", text: "Có lỗi xảy ra!" });
    } finally {
      setSaving(false);
    }
  };

  const handleChannelToggle = (region, channelId) => {
    setChannelsByRegion((prev) => ({
      ...prev,
      [region]: prev[region].map((ch) =>
        ch.id === channelId ? { ...ch, enabled: !ch.enabled } : ch,
      ),
    }));
    setMessage(null);
  };

  const handleCutoffChange = (region, value) => {
    setCutoffTimes((prev) => ({
      ...prev,
      [region]: value,
    }));
    setMessage(null);
  };

  const handleSelectAllChannels = (region, selectAll) => {
    setChannelsByRegion((prev) => ({
      ...prev,
      [region]: prev[region].map((ch) => ({ ...ch, enabled: selectAll })),
    }));
    setMessage(null);
  };

  const handleSaveChannels = async (region) => {
    setSavingChannels((prev) => ({ ...prev, [region]: true }));
    setMessage(null);
    try {
      const enabledIds = channelsByRegion[region]
        .filter((ch) => ch.enabled)
        .map((ch) => ch.id);
      const result = await updateLotoChannelSetting(
        cookies.lottmekong,
        region,
        enabledIds,
      );
      if (result && result.status === 1) {
        setMessage({
          type: "success",
          text: `Cập nhật đài ${region} thành công!`,
        });
      } else {
        setMessage({ type: "danger", text: `Cập nhật đài ${region} thất bại!` });
      }
    } catch (err) {
      console.log(`updateChannels ${region} error`, err);
      setMessage({ type: "danger", text: "Có lỗi xảy ra!" });
    } finally {
      setSavingChannels((prev) => ({ ...prev, [region]: false }));
    }
  };

  const regions = [
    { key: "MB", label: "Lô tô Miền Bắc" },
    { key: "MT", label: "Lô tô Miền Trung" },
    { key: "MN", label: "Lô tô Miền Nam" },
  ];

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 300 }}
      >
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <div className="d-xl-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-xl-0">
          <Breadcrumb
            className="d-none d-md-inline-block"
            listProps={{ className: "breadcrumb-dark breadcrumb-transparent" }}
          >
            <Breadcrumb.Item>
              <FontAwesomeIcon icon={faHome} />
            </Breadcrumb.Item>
            <Breadcrumb.Item>Loto</Breadcrumb.Item>
            <Breadcrumb.Item active>Cài đặt</Breadcrumb.Item>
          </Breadcrumb>
          <h4>Cài đặt Lô tô</h4>
        </div>
      </div>

      <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav.Item>
          <Nav.Link eventKey="setting">Cài đặt vùng miền</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="channel">Gán đài theo thứ</Nav.Link>
        </Nav.Item>
      </Nav>

      {activeTab === "channel" && (
        <>
          <Nav variant="tabs" className="mb-3" activeKey={channelRegion} onSelect={(k) => setChannelRegion(k)}>
            {CHANNEL_REGIONS.map((r) => (
              <Nav.Item key={r.key}>
                <Nav.Link eventKey={r.key}>{r.label}</Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
          <ChannelTable region={channelRegion} />
        </>
      )}

      {activeTab === "setting" && (
      <Card border="light" className="shadow-sm">
        <Card.Header>
          <h5 className="mb-0">Bật/Tắt vùng miền</h5>
        </Card.Header>
        <Card.Body>
          {message && (
            <Alert
              variant={message.type}
              onClose={() => setMessage(null)}
              dismissible
            >
              {message.text}
            </Alert>
          )}

          {regions.map((region) => {
            const regionChannels = channelsByRegion[region.key] || [];
            const allSelected =
              regionChannels.length > 0 &&
              regionChannels.every((ch) => ch.enabled);
            const noneSelected =
              regionChannels.length === 0 ||
              regionChannels.every((ch) => !ch.enabled);

            return (
              <div key={region.key} className="border-bottom pb-3 mb-3">
                <div className="d-flex justify-content-between align-items-center py-2">
                  <div>
                    <h6 className="mb-0">{region.label}</h6>
                    <small className="text-muted">
                      {config[region.key] ? "Đang bật" : "Đang tắt"}
                    </small>
                  </div>
                  <Form.Check
                    type="switch"
                    id={`switch-${region.key}`}
                    checked={config[region.key]}
                    onChange={() => handleToggle(region.key)}
                  />
                </div>

                <Collapse in={config[region.key]}>
                  <div>
                    <div className="ms-3 mt-2 mb-3" style={{ maxWidth: 220 }}>
                      <Form.Label className="small text-muted mb-1">
                        Giờ cutoff
                      </Form.Label>
                      <Form.Control
                        type="time"
                        value={cutoffTimes[region.key] || DEFAULT_CUTOFF_TIMES[region.key]}
                        onChange={(e) => handleCutoffChange(region.key, e.target.value)}
                      />
                    </div>
                    {regionChannels.length > 0 ? (
                      <div className="ms-3 mt-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <small className="text-muted fw-bold">
                            Chọn đài ({regionChannels.filter((ch) => ch.enabled).length}/{regionChannels.length})
                          </small>
                          <div>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 me-3"
                              onClick={() =>
                                handleSelectAllChannels(region.key, true)
                              }
                              disabled={allSelected}
                            >
                              Chọn tất cả
                            </Button>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0"
                              onClick={() =>
                                handleSelectAllChannels(region.key, false)
                              }
                              disabled={noneSelected}
                            >
                              Bỏ chọn tất cả
                            </Button>
                          </div>
                        </div>
                        {regionChannels.map((channel) => (
                          <Form.Check
                            key={channel.id}
                            type="checkbox"
                            id={`channel-${channel.id}`}
                            label={channel.name}
                            checked={channel.enabled}
                            onChange={() =>
                              handleChannelToggle(region.key, channel.id)
                            }
                            className="mb-1"
                          />
                        ))}
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="mt-2"
                          onClick={() => handleSaveChannels(region.key)}
                          disabled={savingChannels[region.key]}
                        >
                          {savingChannels[region.key] ? (
                            <Spinner
                              animation="border"
                              size="sm"
                              className="me-1"
                            />
                          ) : (
                            <FontAwesomeIcon icon={faSave} className="me-1" />
                          )}
                          Lưu đài {region.key}
                        </Button>
                      </div>
                    ) : (
                      <div className="ms-3 mt-2">
                        <small className="text-muted">
                          Không có đài nào
                        </small>
                      </div>
                    )}
                  </div>
                </Collapse>
              </div>
            );
          })}

          <div className="mt-4">
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <FontAwesomeIcon icon={faSave} className="me-2" />
              )}
              Lưu cài đặt vùng miền
            </Button>
          </div>
        </Card.Body>
      </Card>
      )}
    </>
  );
};

export default LotoSetting;
